import { useState, useEffect, type ComponentProps } from 'react';

import { Stack } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { useTimezoneContext } from '../../../../../fenrir-provider/timezone';
import { useFenrirTranslation } from '../../../../../locales/use-fenrir-translation';
import type { GridFilterInputValueProps, GridFilterItem } from '../../../index';

interface InputProps extends GridFilterInputValueProps {
  InputProps?: {
    inputProps: {
      min?: Dayjs;
      max?: Dayjs;
    };
  };
}

type InputType = ComponentProps<typeof DateTimePicker>['value'][];

const defaultValue: InputType = [null, null];

// convert to known dayjs object
const convertItemToDate = (item: GridFilterItem) => {
  if (item.value && Array.isArray(item.value) && item.value.length === 2) {
    const start = dayjs(item.value[0]);
    const end = dayjs(item.value[1]);
    if (start.isValid() && end.isValid()) return [start, end];
  }
  return defaultValue;
};

export const GridFilterInputDateRange = (props: InputProps) => {
  const { t } = useFenrirTranslation({ keyPrefix: 'dataGrid' });
  const { item, applyValue, InputProps } = props;
  const [filterValueState, setFilterValueState] = useState(
    convertItemToDate(item),
  );
  const { muiTimezone } = useTimezoneContext();

  const min = InputProps?.inputProps.min;
  const max = InputProps?.inputProps.max;

  useEffect(() => {
    setFilterValueState(convertItemToDate(item));
  }, [item]);

  const handleLowerFilterChange = (newStartDate: Dayjs | null) => {
    if (newStartDate) {
      setFilterValueState([newStartDate, filterValueState[1]]);
      if (newStartDate.isBefore(filterValueState[1])) {
        applyValue({ ...item, value: [newStartDate, filterValueState[1]] });
      }
    }
  };

  const handleUpperFilterChange = (newEndDate: Dayjs | null) => {
    if (newEndDate) {
      setFilterValueState([filterValueState[0], newEndDate]);
      if (newEndDate.isAfter(filterValueState[0])) {
        applyValue({ ...item, value: [filterValueState[0], newEndDate] });
      }
    }
  };

  return (
    <Stack direction="row" spacing={1}>
      <DateTimePicker
        value={filterValueState[0]}
        onChange={handleLowerFilterChange}
        label={t('start')}
        timezone={muiTimezone}
        slotProps={{
          textField: {
            InputLabelProps: { shrink: true },
          },
        }}
        {...(min ? { minDate: min } : {})}
        {...(max ? { maxDate: max } : {})}
      />
      <DateTimePicker
        value={filterValueState[1]}
        onChange={handleUpperFilterChange}
        label={t('end')}
        timezone={muiTimezone}
        slotProps={{
          textField: {
            InputLabelProps: { shrink: true },
          },
        }}
        {...(min ? { minDate: min } : {})}
        {...(max ? { maxDate: max } : {})}
      />
    </Stack>
  );
};
